import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { iAxios } from '../../lib/utils';
import { useConf } from '../../lib/hooks';
import { LogoMark } from '../ds/LogoMark';

const STEPS = [
    {
        title: 'Sign up and get started',
        body: 'Create an account with your company email. If your employer has already set up the workspace, sign in with any of the available providers.'
    },
    {
        title: 'Install the Copilot app',
        body: 'Head to the downloads section and install Copilot for your operating system. It runs quietly in the background, tracking screen activity across apps and websites.'
    },
    {
        title: 'Start Copilot from your dashboard',
        body: 'Your dashboard gives an overview of productive time. Once you have signed in and granted location permission, you can launch Copilot.'
    },
    {
        title: 'Act on AI recommendations',
        body: 'Focuz analyses your activity to surface patterns and bottlenecks, then suggests focused work sessions and ways to cut distractions during critical hours.'
    }
];

const Login = () => {
    const [mode] = useState('initial');
    const [error, setError] = useState(null);
    const { provider } = useParams();
    const { loginButtons } = useConf();
    const [sp] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
        const code = sp.get('code');
        if (code) {
            iAxios({
                method: 'post',
                url: `/api/v1/auth/${provider}`,
                data: sp
            }).then(resp => {
                const { data } = resp.data;
                localStorage.setItem('access_token', data.jwt);
                navigate(data.nextUrl || '/dashboard');
            }).catch(ex => {
                console.error(ex);
                setError(ex.response?.data?.message || ex.message);
            });
        }
    }, [sp, provider]);

    return (
        <div className="ds-scope login">
            <div className="login__bar">
                <LogoMark className="login__logo" size={32} />
                <span className="login__wordmark">Focuz</span>
            </div>

            <div className="login__grid">
                <section className="login__lede">
                    <span className="ds-badge is-accent login__eyebrow">Productivity, measured</span>
                    <h1 className="login__headline">
                        Scale your team's productivity to <em>new heights</em>.
                    </h1>
                    <p className="login__sub">
                        Focuz monitors screen activity and turns it into actionable insight — so individuals
                        manage their time better and teams work smarter, not harder, with AI-driven
                        recommendations tailored to each workflow.
                    </p>

                    <ol className="login__steps">
                        {STEPS.map(step => (
                            <li className="login__step" key={step.title}>
                                <div>
                                    <h4>{step.title}</h4>
                                    <p>{step.body}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>

                <section className="login__auth" aria-label="Sign in">
                    <LogoMark className="login__auth-logo" size={56} title="Focuz logo" />
                    <h3>Welcome back</h3>
                    <p className="login__auth-note">Sign in to your Focuz dashboard</p>

                    {error && <div className="login__error" role="alert">{error}</div>}

                    {mode === 'progress' ? (
                        <div className="login__progress">
                            <span className="ds-spinner" /> Signing you in…
                        </div>
                    ) : (
                        <div className="login__providers">
                            {loginButtons.map(item => (
                                <a
                                    key={item.value}
                                    className={`ds-btn is-primary is-block btn-${item.value}`}
                                    href={`/auth/${item.value}`}
                                >
                                    {item.name}
                                </a>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            <footer className="login__footer">
                <Link to="/pages?path=faq">FAQ</Link>
                <Link to="/pages?path=download">Download</Link>
                <Link to="/pages?path=terms">Terms of Service</Link>
                <Link to="/pages?path=privacy">Privacy</Link>
                <Link to="/login">Home</Link>
            </footer>
        </div>
    );
};

export default Login;
